import type { ReservationMissingItem } from '../../hooks/useReservations'

interface MissingItemsPrintInput {
	reservationNumber: string
	customerName?: string | null
	missing: ReservationMissingItem[]
}

function escapeHtml(value: unknown): string {
	const str = value == null ? '' : String(value)
	return str
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}

function reasonLabel(item: ReservationMissingItem): string {
	if (item.reason === 'insufficient_stock') {
		return item.transferred > 0 ? 'Partially out of stock' : 'Out of stock'
	}
	return 'Skipped (not found)'
}

/**
 * Opens a print-to-PDF slip listing the items that could not be fully fulfilled, with full
 * product detail (name / reference / color / size / sku) and requested vs available quantities.
 * Mirrors the window.open + window.print pattern used by the reservation slip.
 */
export function printMissingItems({ reservationNumber, customerName, missing }: MissingItemsPrintInput): void {
	if (!missing || missing.length === 0) return

	const printWindow = window.open('', '_blank', 'width=900,height=700')
	if (!printWindow) return

	const rows = missing
		.map(
			(m) => `
				<tr>
					<td>${escapeHtml(m.name || '-')}</td>
					<td>${escapeHtml(m.reference_code || '-')}</td>
					<td>${escapeHtml(m.sku || '-')}</td>
					<td>${escapeHtml(m.color || '-')}</td>
					<td>${escapeHtml(m.size || '-')}</td>
					<td class="num">${escapeHtml(m.requested)}</td>
					<td class="num">${escapeHtml(m.transferred)}</td>
					<td class="reason">${escapeHtml(reasonLabel(m))}</td>
				</tr>`
		)
		.join('')

	const html = `<!DOCTYPE html>
<html>
<head>
	<meta charset="utf-8" />
	<title>Missing Items - ${escapeHtml(reservationNumber)}</title>
	<style>
		* { box-sizing: border-box; }
		body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; padding: 40px; color: #1f2937; font-size: 14px; }
		.header { border-bottom: 3px solid #b91c1c; padding-bottom: 16px; margin-bottom: 24px; }
		.title { font-size: 26px; font-weight: 700; color: #991b1b; }
		.subtitle { color: #6b7280; margin-top: 4px; }
		.meta { margin: 16px 0 24px; color: #374151; }
		.meta strong { color: #111827; }
		table { width: 100%; border-collapse: collapse; margin-top: 8px; }
		th { text-align: left; padding: 10px 12px; background: #fef2f2; color: #991b1b; font-size: 12px; text-transform: uppercase; letter-spacing: 0.03em; border-bottom: 2px solid #fecaca; }
		td { padding: 10px 12px; border-bottom: 1px solid #f3f4f6; }
		td.num, th.num { text-align: center; }
		td.reason { color: #991b1b; font-weight: 600; }
		.footer { margin-top: 28px; color: #9ca3af; font-size: 12px; }
		@media print { body { padding: 20px; } .no-print { display: none; } }
	</style>
</head>
<body>
	<div class="header">
		<div class="title">Missing Items</div>
		<div class="subtitle">Reservation ${escapeHtml(reservationNumber)}</div>
	</div>
	<div class="meta">
		${customerName ? `<div><strong>Customer:</strong> ${escapeHtml(customerName)}</div>` : ''}
		<div><strong>Printed:</strong> ${escapeHtml(new Date().toLocaleString())}</div>
		<div><strong>Items not fully fulfilled:</strong> ${missing.length}</div>
	</div>
	<table>
		<thead>
			<tr>
				<th>Product</th>
				<th>Reference</th>
				<th>SKU</th>
				<th>Color</th>
				<th>Size</th>
				<th class="num">Requested</th>
				<th class="num">Fulfilled</th>
				<th>Reason</th>
			</tr>
		</thead>
		<tbody>${rows}</tbody>
	</table>
	<div class="footer">Generated by the Reservation Terminal.</div>
	<script>window.onload = function () { window.print(); };</script>
</body>
</html>`

	printWindow.document.write(html)
	printWindow.document.close()
}
